import { getNetworkStatus } from './networkMonitor';

export type ConnectionSpeed = 'very-slow' | 'slow' | 'medium' | 'fast';

/**
 * Detects the current connection speed based on network conditions
 * @returns ConnectionSpeed classification
 */
export function detectConnectionSpeed(): ConnectionSpeed {
  const networkStatus = getNetworkStatus();
  
  // If offline, consider it very slow
  if (!networkStatus.online) {
    return 'very-slow';
  }
  
  // Check for save-data mode
  if (networkStatus.saveData) {
    return 'slow';
  }
  
  // Classify based on effective connection type
  switch (networkStatus.effectiveType) {
    case 'slow-2g':
      return 'very-slow';
    case '2g':
      return 'slow';
    case '3g':
      // For 3G, check RTT to determine if it's slow or medium
      if (networkStatus.rtt && networkStatus.rtt > 500) {
        return 'slow';
      }
      return 'medium';
    case '4g':
      // For 4G, classify based on RTT and bandwidth
      if (networkStatus.rtt && networkStatus.rtt > 300) {
        return 'medium';
      }
      if (networkStatus.downlink && networkStatus.downlink < 1.5) {
        return 'medium';
      }
      return 'fast';
    default:
      // For unknown types, make a best guess based on RTT if available
      if (networkStatus.rtt) {
        if (networkStatus.rtt > 800) return 'very-slow';
        if (networkStatus.rtt > 400) return 'slow';
        if (networkStatus.rtt > 200) return 'medium';
      }
      
      // Default to medium as a safe fallback
      return 'medium';
  }
}

/**
 * Detects if the current connection should be treated as a slow connection
 */
export function isSlowConnection(): boolean {
  const speed = detectConnectionSpeed();
  return speed === 'very-slow' || speed === 'slow';
}

/**
 * Returns a simple human-readable label for the connection speed
 */
export function getConnectionSpeedLabel(speed: ConnectionSpeed): string {
  switch (speed) {
    case 'very-slow': return 'Very slow connection';
    case 'slow': return 'Slow connection';
    case 'medium': return 'Medium connection';
    case 'fast': return 'Fast connection';
  }
}

/**
 * Calculate image size based on network conditions
 * @param defaultSize The default size to use when network is fast
 * @returns Adjusted size based on connection speed
 */
export function getNetworkAwareImageSize(defaultSize: number): number {
  const connectionSpeed = detectConnectionSpeed();
  
  // Adjust size based on connection speed
  switch (connectionSpeed) {
    case 'very-slow':
      return Math.min(defaultSize, 320); // Very small images for very slow connections
    case 'slow':
      return Math.min(defaultSize, 480); // Small images for slow connections
    case 'medium':
      return Math.min(defaultSize, 720); // Medium images for medium connections
    case 'fast':
      return defaultSize; // Use the full size for fast connections
  }
}

/**
 * Calculate image quality based on network conditions
 * @param defaultQuality Optional default quality (1-100)
 * @returns Adjusted quality based on connection speed
 */
export function getNetworkAwareImageQuality(defaultQuality?: number): number {
  const connectionSpeed = detectConnectionSpeed();
  
  // If a default quality was provided, adjust it based on connection
  if (defaultQuality !== undefined) {
    switch (connectionSpeed) {
      case 'very-slow':
        return Math.min(defaultQuality, 30);
      case 'slow':
        return Math.min(defaultQuality, 50);
      case 'medium':
        return Math.min(defaultQuality, 70);
      case 'fast':
        return defaultQuality;
    }
  }
  
  // Otherwise use these default values based on connection
  switch (connectionSpeed) {
    case 'very-slow':
      return 30; // Very low quality for very slow connections
    case 'slow':
      return 50; // Low quality for slow connections
    case 'medium':
      return 70; // Medium quality for medium connections
    case 'fast':
      return 85; // High quality for fast connections
  }
}
